export interface NavigationItem {
  key: string
  label: string
  group?: string
  shortLabel?: string
}

interface BootstrapLike {
  capabilities: Record<string, boolean>
}

export type StaffViewKey =
  | 'dashboard'
  | 'patients'
  | 'payments'
  | 'schedule'
  | 'constructor'
  | 'plans'
  | 'communications'
  | 'gift-certificates'
  | 'diagnocat'
  | 'promotions'
  | 'content'
  | 'analytics'
  | 'users'
  | 'reports'
  | 'documents'
  | 'payment-links'
  | 'media'

const STAFF_VIEW_PERMISSION_MAP: Record<StaffViewKey, string[]> = {
  dashboard: ['dashboard'],
  patients: ['patients'],
  payments: ['payments', 'staff_payments'],
  schedule: ['schedule'],
  constructor: ['constructor'],
  plans: ['plans', 'plans_mis'],
  communications: ['communications'],
  'gift-certificates': ['gift_certificates'],
  diagnocat: ['diagnocat'],
  promotions: ['promotions'],
  content: ['content'],
  analytics: ['analytics'],
  users: ['users'],
  reports: ['reports_imaging', 'reports_mcard', 'users'],
  // 03.05.26: модуль «Документы» (рассрочка / налоговая справка) — proxy в МИС.
  documents: ['documents', 'documents_create'],
  // 11.05.26: платёжные ссылки
  'payment-links': ['payments', 'staff_payments'],
  // 20.05.26: медиа-репозиторий — только суперадмин
  media: ['users'],
}

function hasAnyPermission(staffPermissions: string[] | undefined, requiredPermissions: string[]): boolean {
  if (!staffPermissions || staffPermissions.length === 0) {
    return false
  }

  const granted = new Set(staffPermissions)
  if (granted.has('admin') || granted.has('*') || granted.has('all')) {
    return true
  }

  return requiredPermissions.some((permission) => granted.has(permission))
}

export function canAccessStaffView(key: string, staffPermissions?: string[]): boolean {
  const required = STAFF_VIEW_PERMISSION_MAP[key as StaffViewKey]
  if (!required) {
    return true
  }

  return hasAnyPermission(staffPermissions, required)
}

export function buildPatientNavigation(bootstrap: BootstrapLike): NavigationItem[] {
  const items: NavigationItem[] = [
    { key: 'dashboard', label: 'Главная', group: 'core', shortLabel: 'Главная' },
    { key: 'appointments', label: 'Мои записи', group: 'core', shortLabel: 'Записи' },
    { key: 'history', label: 'История визитов', group: 'core', shortLabel: 'История' },
    { key: 'plans', label: 'Планы лечения', group: 'core', shortLabel: 'Планы' },
    { key: 'payments', label: 'Платежи', group: 'core', shortLabel: 'Платежи' },
    { key: 'profile', label: 'Данные пациента', group: 'core', shortLabel: 'Профиль' },
  ]

  if (bootstrap.capabilities.supportsOnlineAppointments) {
    items.splice(1, 0, { key: 'booking', label: 'Онлайн-запись', group: 'core', shortLabel: 'Запись' })
  }

  if (bootstrap.capabilities.supportsCommunicationPreferences) {
    items.push({ key: 'communication-preferences', label: 'Каналы связи', group: 'core', shortLabel: 'Связь' })
  }

  if (bootstrap.capabilities.supportsMessages) {
    items.push({ key: 'messages', label: 'Сообщения', group: 'core', shortLabel: 'Inbox' })
  }

  if (bootstrap.capabilities.supportsChat) {
    items.push({ key: 'chat', label: 'Чат', group: 'core', shortLabel: 'Чат' })
  }

  if (bootstrap.capabilities.supportsGiftCertificates) {
    items.push({ key: 'gift-certificates', label: 'Сертификат', group: 'info', shortLabel: 'Сертификат' })
  }

  items.push({ key: 'useful', label: 'Полезно', group: 'core', shortLabel: 'Инфо' })

  if (bootstrap.capabilities.supportsDmsGuide) {
    items.push({ key: 'dms', label: 'ДМС', group: 'info', shortLabel: 'ДМС' })
  }

  if (bootstrap.capabilities.supportsYandexSplit) {
    items.push({ key: 'yandex-split', label: 'Яндекс Сплит', group: 'info', shortLabel: 'Сплит' })
  }

  if (bootstrap.capabilities.supportsInstallments) {
    items.push({ key: 'installment', label: 'Рассрочка', group: 'info', shortLabel: 'Рассрочка' })
  }

  if (bootstrap.capabilities.supportsCorporatePrograms) {
    items.push({ key: 'corporate', label: 'Корп. программа', group: 'info', shortLabel: 'Корп.' })
  }

  if (bootstrap.capabilities.supportsQuestionnaires) {
    items.push({ key: 'questionnaires', label: 'Анкеты', group: 'info', shortLabel: 'Анкеты' })
  }

  if (bootstrap.capabilities.supportsGuestCatalog) {
    items.push(
      { key: 'doctors', label: 'Врачи', group: 'guest', shortLabel: 'Врачи' },
      { key: 'services', label: 'Услуги', group: 'guest', shortLabel: 'Услуги' },
      { key: 'sales', label: 'Акции', group: 'guest', shortLabel: 'Акции' },
    )
  }

  if (bootstrap.capabilities.supportsPromotions) {
    items.push({ key: 'promotions', label: 'Промо и акции', group: 'marketing', shortLabel: 'Промо' })
  }

  if (bootstrap.capabilities.supportsContentLibrary) {
    items.push({ key: 'content', label: 'Контент', group: 'marketing', shortLabel: 'Контент' })
  }

  if (bootstrap.capabilities.supportsReviews) {
    items.push({ key: 'reviews', label: 'Отзывы', group: 'marketing', shortLabel: 'Отзывы' })
  }

  if (bootstrap.capabilities.supportsActivityFeed) {
    items.push({ key: 'activity', label: 'Активность', group: 'marketing', shortLabel: 'Активность' })
  }

  if (bootstrap.capabilities.supportsBusinessLeads) {
    items.push({ key: 'leads', label: 'Заявки', group: 'marketing', shortLabel: 'Заявки' })
  }

  return items
}

export function buildStaffNavigation(bootstrap: BootstrapLike, staffPermissions?: string[]): NavigationItem[] {
  const items: NavigationItem[] = [
    { key: 'dashboard', label: 'Дашборд', group: 'primary', shortLabel: 'Дашборд' },
    { key: 'patients', label: 'Пациенты', group: 'primary', shortLabel: 'Пациенты' },
  ]

  if (bootstrap.capabilities.supportsStaffPayments) {
    items.push({ key: 'payments', label: 'Платежи', group: 'primary', shortLabel: 'Платежи' })
  }

  items.push(
    { key: 'schedule', label: 'Расписание', group: 'primary', shortLabel: 'Расписание' },
    { key: 'constructor', label: 'Конструктор', group: 'primary', shortLabel: 'Конструктор' },
    { key: 'plans', label: 'Планы МИС', group: 'primary', shortLabel: 'Планы МИС' },
    { key: 'communications', label: 'Переписки', group: 'primary', shortLabel: 'Переписки' },
  )

  if (bootstrap.capabilities.supportsGiftCertificates) {
    items.push({ key: 'gift-certificates', label: 'Сертификаты', group: 'primary', shortLabel: 'Сертификаты' })
  }

  if (bootstrap.capabilities.supportsDiagnocat) {
    items.push({ key: 'diagnocat', label: 'Diagnocat', group: 'operations', shortLabel: 'Diagnocat' })
  }

  if (bootstrap.capabilities.supportsStaffPromotions) {
    items.push({ key: 'promotions', label: 'Акции', group: 'operations', shortLabel: 'Акции' })
  }

  if (bootstrap.capabilities.supportsStaffContent) {
    items.push({ key: 'content', label: 'Контент ЛК', group: 'operations', shortLabel: 'Контент' })
  }

  if (bootstrap.capabilities.supportsStaffAnalytics) {
    items.push({ key: 'analytics', label: 'Аналитика', group: 'operations', shortLabel: 'Аналитика' })
  }

  if (bootstrap.capabilities.supportsStaffReports && canAccessStaffView('reports', staffPermissions)) {
    items.push({ key: 'reports', label: 'Отчёты', group: 'operations', shortLabel: 'Отчёты' })
  }

  // 03.05.26: модуль «Документы» — proxy в MIS Documents API. Видимость
  // регулируется permission'ами documents / documents_create (см. STAFF_VIEW_PERMISSION_MAP).
  items.push({ key: 'documents', label: 'Документы', group: 'operations', shortLabel: 'Документы' })

  // 20.05.26: медиа-репозиторий (только users/superadmin)
  items.push({ key: 'media', label: 'Медиа', group: 'operations', shortLabel: 'Медиа' })

  items.push({ key: 'users', label: 'Пользователи', group: 'operations', shortLabel: 'Пользователи' })

  if (!staffPermissions) {
    return items
  }

  return items.filter((item) => canAccessStaffView(item.key, staffPermissions))
}
